import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'primaryBeneficiariesForm',
    'contingentBeneficiariesForm',
    'newBeneficiaryRow',
    'beneficiaryRow',
    'primaryShareInput',
    'contingentShareInput',
    'primaryShareTotal',
    'contingentShareTotal',
    'submitButton',
  ];

  primaryBeneficiariesFormTarget: HTMLFormElement;

  contingentBeneficiariesFormTarget: HTMLFormElement;

  newBeneficiaryRowTarget: HTMLElement;

  beneficiaryRowTarget: HTMLElement;

  primaryShareInputTargets: HTMLInputElement[];

  contingentShareInputTargets: HTMLInputElement[];

  primaryShareTotalTarget: HTMLElement;

  contingentShareTotalTarget: HTMLElement;

  submitButtonTarget: HTMLButtonElement;

  initialize() {
    this.handleChangeBeneficiaryShare();
  }

  addPrimaryBeneficiary(e: Event) {
    e.preventDefault();
    this.addBeneficiary({ parentForm: this.primaryBeneficiariesFormTarget, primary: true });
    this.handleChangeBeneficiaryShare();
  }

  addContingentBeneficiary(e: Event) {
    e.preventDefault();
    this.addBeneficiary({ parentForm: this.contingentBeneficiariesFormTarget, primary: false });
    this.handleChangeBeneficiaryShare();
  }

  removeBeneficiary(e: Event) {
    const rowToRemove = this.currentRow(e.currentTarget!);

    if (rowToRemove) {
      rowToRemove.remove();
    }

    this.handleChangeBeneficiaryShare();
  }

  handleChangeBeneficiaryRelationship(e: Event) {
    const currentRow = this.currentRow(e.currentTarget!);
    const isDisabled = this.shouldDisableBirthdateInput((e.currentTarget as HTMLInputElement).value);

    if (currentRow) {
      this.setDateOfBirthField(currentRow, isDisabled);
      this.setTaxIdField(currentRow, !isDisabled);
    }
  }

  handleChangeBeneficiaryShare() {
    this.updateShareTotal(this.primaryShareTotalTarget, this.primaryShareInputTargets);
    this.updateShareTotal(this.contingentShareTotalTarget, this.contingentShareInputTargets);
    this.updateSubmitButton();
  }

  addBeneficiary({ parentForm, primary }: { parentForm: HTMLFormElement; primary: boolean }) {
    const uniqueKey = this.generateUniqueKey();
    const row = this.newRow();

    this.replaceFormFieldKeys(row, uniqueKey);
    this.setPrimaryField(row, primary);
    this.setShareField(row, primary);
    this.setDateOfBirthField(row, false);
    this.setTaxIdField(row, true);

    row.removeAttribute('hidden');
    parentForm.appendChild(row);

    const { id } = row.querySelector("input[name$='[date_of_birth]']") || {};
    if (id) {
      window.createMask(id, 'date');
    }
  }

  setPrimaryField(row: HTMLElement, primary: boolean) {
    const primaryField = row.querySelector("input[name$='[primary]']") as HTMLInputElement;
    primaryField.value = `${String(primary)}`;
  }

  setShareField(row: HTMLElement, primary: boolean) {
    const targetName = primary ? 'primaryShareInput' : 'contingentShareInput';
    const input = row.querySelector("input[name$='[share]']") as HTMLElement;
    input.dataset.advisedSignupBeneficiariesFormTarget = targetName;
  }

  setDateOfBirthField(row: HTMLElement, isDisabled: boolean) {
    const inputField = row.querySelector("input[name$='[date_of_birth]']") as HTMLInputElement;

    if (isDisabled) {
      inputField.value = '';
      inputField.setAttribute('disabled', 'true');
    } else {
      inputField.classList.remove('disabled');
      inputField.removeAttribute('disabled');
    }
  }

  setTaxIdField(row: HTMLElement, isDisabled: boolean) {
    const inputField = row.querySelector("input[name$='[tax_identifier]']") as HTMLInputElement;

    if (isDisabled) {
      inputField.value = '';
      inputField.setAttribute('disabled', 'true');
    } else {
      inputField.classList.remove('disabled');
      inputField.removeAttribute('disabled');
    }
  }

  replaceFormFieldKeys(row: HTMLElement, uniqueKey: number) {
    const fields: NodeListOf<HTMLInputElement> = row.querySelectorAll('input, select');
    fields.forEach(field => {
      const uniqueId = field.id.replace(/[0-9]+/g, uniqueKey.toString());
      const uniqueName = field.name.replace(/[0-9]+/g, uniqueKey.toString());
      field.setAttribute('id', uniqueId);
      field.setAttribute('name', uniqueName);
      field.removeAttribute('disabled');
    });
  }

  generateUniqueKey() {
    const date = new Date();
    return date.getTime();
  }

  currentRow(currentTarget: EventTarget) {
    return (currentTarget as HTMLElement).closest(
      "[data-advised-signup-beneficiaries-form-target='beneficiaryRow']"
    ) as HTMLElement;
  }

  newRow() {
    return this.newBeneficiaryRowTarget.cloneNode(true) as HTMLElement;
  }

  shouldDisableBirthdateInput(relationshipInputValue: string) {
    return !['friend', 'spouse'].includes(relationshipInputValue);
  }

  updateShareTotal(totalTarget: HTMLElement, shareTotalTargets: HTMLInputElement[]) {
    if (shareTotalTargets.length > 0) {
      const shareTotal = this.calculateShareTotals(shareTotalTargets);
      totalTarget.innerHTML = `Total: ${shareTotal}%`;
      if (shareTotal !== 100) {
        totalTarget.classList.add(`u-color-bmtOrange`);
      } else {
        totalTarget.classList.remove(`u-color-bmtOrange`);
      }
    } else {
      totalTarget.innerHTML = ``;
    }
  }

  calculateShareTotals(shareInputs: HTMLInputElement[]) {
    const total = shareInputs
      .map(input => {
        return parseFloat(input.value) || 0;
      })
      .reduce((sum, value) => {
        return sum + value;
      });

    return total;
  }

  updateSubmitButton() {
    const isPrimaryValid =
      this.primaryShareInputTargets.length === 0 || this.calculateShareTotals(this.primaryShareInputTargets) === 100;
    const isContingentValid =
      this.contingentShareInputTargets.length === 0 ||
      this.calculateShareTotals(this.contingentShareInputTargets) === 100;
    this.submitButtonTarget.disabled = !(isPrimaryValid && isContingentValid);
  }
}
